"use client";

import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useClickOutsideHandler } from '@/hooks/useClickOutsideHandler';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Heart, X, Plus, ShoppingCart, Store as StoreIcon, Loader2 } from '@/components/icons';
import { Button } from '@/components/ui/button';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { formatCurrency, cn } from '@/lib/utils';
import { useFavorites, type FavoritedProduct } from '@/hooks/useFavorites';
import { usePullToClose } from '@/hooks/usePullToClose';
import { getTempCartAsItems, removeFromTempCart, updateTempCartItemQuantity } from '@/lib/tempCart';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { motion, AnimatePresence } from 'framer-motion';
import { getProductById } from '@/lib/supabase/products';
import { useContentSidebar } from '@/context/ContentSidebarContext';
import { useCheckout } from '@/context/CheckoutContext';
import { useToast } from '@/components/ui/use-toast';
import { useSession } from '@/context/SessionContext';
import { getCartForUser, removeCartItem, updateCartItem } from '@/lib/supabase/carts';
import { usePlayFavoriteSound } from '@/hooks/usePlayFavoriteSound';
import { useVibrate } from '@/hooks/useVibrate';
import { QuantitySelector } from '@/components/QuantitySelector';
import { useScrollLock } from '@/hooks/useScrollLock';

interface FavoritesSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onAddToCart?: (productId: string) => void;
  onAddToCartWithQuantity?: (productId: string, quantity: number) => void;
  onAddMultipleItemsToCart?: (items: Array<{ productId: string; quantity: number; storeId: string }>) => Promise<void>;
  onPlayBeep?: (type: 'add' | 'decrease') => void;
  onVibrate?: (type: 'add' | 'decrease') => void;
}

interface GroupedFavorites {
  [storeId: string]: {
    store_name: string;
    products: FavoritedProduct[];
  };
}

export const FavoritesSidebar: React.FC<FavoritesSidebarProps> = ({
  isOpen,
  onClose,
  onAddToCart,
  onAddToCartWithQuantity,
  onAddMultipleItemsToCart,
  onPlayBeep,
  onVibrate
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [cartItems, setCartItems] = useState<Array<{ id: string; product_id: string; quantity: number }>>([]);
  const [loadingProductId, setLoadingProductId] = useState<string | null>(null);
  const [loadingStoreId, setLoadingStoreId] = useState<string | null>(null);
  const [updatingProductIds, setUpdatingProductIds] = useState<Set<string>>(new Set());
  const [addingProductIds, setAddingProductIds] = useState<Set<string>>(new Set());
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncCardDismissed, setSyncCardDismissed] = useState(false);
  const [resolvedProductImages, setResolvedProductImages] = useState<Record<string, string | null>>({});
  const sidebarRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { headerHeight, isDesktop, bottomNavHeight } = useHeaderOffset();
  const { getFavoritesList, isAuthenticated, loadLocalFavorites, isFavorited, toggleFavorite, hasLocalFavoritesToSync, syncLocalFavoritesManually } = useFavorites();
  const { setProductSidebarOpen, setSelectedProduct, productSidebarOpen } = useContentSidebar();
  const { openSidebar: openCheckoutSidebar } = useCheckout();
  const { toast } = useToast();
  const { session } = useSession();
  const playFavoriteSound = usePlayFavoriteSound();
  const vibrate = useVibrate();
  const favorites = getFavoritesList();
  const favoritesIdsKey = favorites.map(f => f.id).join(',');
  const [currentStoreId, setCurrentStoreId] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const storeId = new URLSearchParams(window.location.search).get('storeId');
    setCurrentStoreId(storeId);
  }, [isOpen]);

  useScrollLock(isOpen || isClosing, false, scrollAreaRef);

  const loadCartItems = useCallback(async () => {
    if (session?.user?.id) {
      try {
        const allStoreIds = Array.from(
          new Set(
            favorites
              .map((product) => product.store_id)
              .filter((storeId): storeId is string => Boolean(storeId))
          )
        );

        if (allStoreIds.length === 0) {
          setCartItems(prev => prev.filter(item => item.id.startsWith('pending-')));
          return;
        }

        const carts = await Promise.all(
          allStoreIds.map((storeId) => getCartForUser(session.user.id, storeId))
        );

        const nextCartItems = carts.flatMap((cart) =>
          ((cart?.cart_items as Array<{ id: string; product_id: string; quantity: number }> | undefined) ?? []).map((item) => ({
            id: item.id,
            product_id: item.product_id,
            quantity: item.quantity,
          }))
        );

        setCartItems(prev => {
          const pendingItems = prev.filter(item => item.id.startsWith('pending-'));
          const pendingNotYetConfirmed = pendingItems.filter(pending =>
            !nextCartItems.some(item => item.product_id === pending.product_id)
          );

          return [...nextCartItems, ...pendingNotYetConfirmed];
        });
        return;
      } catch (error) {
        console.error('[FavoritesSidebar] Error loading DB cart:', error);
      }
    }

    const tempItems = getTempCartAsItems();
    setCartItems(
      tempItems.map((item) => ({
        id: item.id,
        product_id: item.product_id,
        quantity: item.quantity ?? 1,
      }))
    );
  }, [favoritesIdsKey, session?.user?.id]);

  // Cargar canasto y mantenerse sincronizado con cambios globales
  useEffect(() => {
    loadCartItems();

    const handleCartUpdate = () => {
      setTimeout(() => {
        void loadCartItems();
      }, 50);
    };

    window.addEventListener('cart-updated', handleCartUpdate);

    return () => {
      window.removeEventListener('cart-updated', handleCartUpdate);
    };
  }, [loadCartItems]);

  // Resolve missing product images lazily so favorites always display photos when available.
  useEffect(() => {
    let cancelled = false;

    const missingImageProducts = favorites.filter((product) => {
      const hasInlineImage = Boolean(product.main_image_url || product.cover_url || (Array.isArray(product.image_urls) && product.image_urls.length > 0));
      return !hasInlineImage && typeof resolvedProductImages[product.id] === 'undefined';
    });

    if (missingImageProducts.length === 0) return;

    const resolveImages = async () => {
      const entries = await Promise.all(
        missingImageProducts.map(async (product) => {
          try {
            const full = await getProductById(product.id);
            const imageUrl = full?.main_image_url || full?.cover_url || (Array.isArray(full?.image_urls) ? full.image_urls[0] : null) || null;
            return [product.id, imageUrl] as const;
          } catch {
            return [product.id, null] as const;
          }
        })
      );

      if (cancelled) return;
      setResolvedProductImages((prev) => {
        const next = { ...prev };
        entries.forEach(([id, url]) => {
          next[id] = url;
        });
        return next;
      });
    };

    void resolveImages();

    return () => {
      cancelled = true;
    };
  }, [favorites, resolvedProductImages]);

  // Verificar si un producto está en el canasto
  const isInCart = (productId: string) => {
    return cartItems.some(item => item.product_id === productId);
  };

  const getCartItemByProductId = useCallback((productId: string) => {
    return cartItems.find(item => item.product_id === productId) ?? null;
  }, [cartItems]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  }, [onClose]);

  // Pull-to-close
  usePullToClose(scrollAreaRef, {
    onClose: handleClose,
    threshold: 80,
    enabled: isOpen && !isDesktop
  });

  // Agregar al canasto: 1 unidad directamente, sin selector
  const handleAddToCart = useCallback((productId: string) => {

    // Marcar como agregando
    setAddingProductIds(prev => new Set([...prev, productId]));

    // Actualizar estado LOCAL inmediatamente para UI feedback
    setCartItems(prev => {
      const exists = prev.some(item => item.product_id === productId);
      if (!exists) {
        return [...prev, { id: `pending-${productId}`, product_id: productId, quantity: 1 }];
      }
      return prev;
    });

    // Agregar al canasto en backend/storage
    if (onAddToCartWithQuantity) {
      onAddToCartWithQuantity(productId, 1);
    } else if (onAddToCart) {
      onAddToCart(productId);
    }

    // Feedback inmediato
    onPlayBeep?.('add');
    onVibrate?.('add');

    // NO remover de favoritos - el producto permanece en la lista
  }, [onAddToCart, onAddToCartWithQuantity, onPlayBeep, onVibrate]);

  // Agregar todos los productos de una tienda al canasto - se agregan en lote al mismo canasto
  const handleAddAllToCart = useCallback(async (storeId: string, products: FavoritedProduct[]) => {

    setLoadingStoreId(storeId);
    
    // Marcar todos los productos como agregando
    setAddingProductIds(prev => new Set([...prev, ...products.map(p => p.id)]));
    
    try {
      if (onAddMultipleItemsToCart) {
        // NEW: Use batch endpoint if available - this prevents race conditions!
        const items = products.map(p => ({
          productId: p.id,
          quantity: 1,
          storeId: p.store_id || storeId
        }));


        // Update UI immediately for all items
        setCartItems(prev => {
          const newItems = [...prev];
          for (const item of items) {
            const exists = newItems.some(ci => ci.product_id === item.productId);
            if (!exists) {
              newItems.push({ id: `pending-${item.productId}`, product_id: item.productId, quantity: item.quantity });
            }
          }
          return newItems;
        });

        // Play sounds and vibration for each added item
        items.forEach(() => {
          onPlayBeep?.('add');
          onVibrate?.('add');
        });

        // Call batch add function
        await onAddMultipleItemsToCart(items);
      } else {
        // FALLBACK: Old method with delays (if batch not available)

        for (const product of products) {
          // Actualizar estado LOCAL primero para feedback UI inmediato
          setCartItems(prev => {
            const exists = prev.some(item => item.product_id === product.id);
            if (!exists) {
              return [...prev, { id: `pending-${product.id}`, product_id: product.id, quantity: 1 }];
            }
            return prev;
          });

          if (onAddToCartWithQuantity) {
            onAddToCartWithQuantity(product.id, 1);
          } else if (onAddToCart) {
            onAddToCart(product.id);
          }
          onPlayBeep?.('add');
          onVibrate?.('add');
          // Delay más largo para evitar que ensureOpenCart cree múltiples canastos
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Abrir checkout y cerrar favoritos al terminar
      handleClose();
      openCheckoutSidebar();
    } catch (err) {
      console.error('[FavoritesSidebar] Error adding all to cart:', err);
      toast({
        title: 'Error',
        description: 'No pudimos agregar los productos al canasto',
        variant: 'destructive'
      });
    } finally {
      setLoadingStoreId(null);
    }
  }, [onAddToCart, onAddToCartWithQuantity, onPlayBeep, onVibrate, toast, handleClose, openCheckoutSidebar]);

  // Remover del canasto
  const handleRemoveFromCart = useCallback(async (productId: string) => {

    try {
      if (session?.user?.id) {
        const cartItem = cartItems.find(item => item.product_id === productId);
        if (!cartItem?.id || cartItem.id.startsWith('pending-')) {
          throw new Error('No encontramos el item real del canasto para removerlo');
        }

        // Usuario logueado: remover del canasto en BD
        await removeCartItem(cartItem.id);
      } else {
        // Usuario no logueado: remover del canasto temporal
        removeFromTempCart(productId);
      }

      // Actualizar estado LOCAL inmediatamente
      setCartItems(prev => prev.filter(item => item.product_id !== productId));

      // Disparar evento para sincronizar canasto en otros componentes
      window.dispatchEvent(new CustomEvent('cart-updated'));
    } catch (err) {
      console.error('[FavoritesSidebar] Error removing from cart:', err);
      toast({
        title: 'Error',
        description: (err as any)?.message || 'No pudimos remover el producto del canasto',
        variant: 'destructive'
      });
    }
  }, [cartItems, session?.user?.id, toast]);

  const handleUpdateQuantity = useCallback(async (
    productId: string,
    cartItemId: string,
    quantityCalculator: (current: number) => number
  ) => {
    const cartItem = getCartItemByProductId(productId);
    if (!cartItem) return;

    const nextQuantity = quantityCalculator(cartItem.quantity);
    setUpdatingProductIds(prev => new Set(prev).add(productId));

    try {
      if (nextQuantity <= 0) {
        await handleRemoveFromCart(productId);
        return;
      }

      if (session?.user?.id) {
        if (cartItemId.startsWith('pending-')) {
          await loadCartItems();
          return;
        }

        await updateCartItem(cartItemId, nextQuantity);
      } else {
        updateTempCartItemQuantity(productId, nextQuantity);
      }

      setCartItems(prev => prev.map(item =>
        item.product_id === productId
          ? { ...item, quantity: nextQuantity }
          : item
      ));

      window.dispatchEvent(new CustomEvent('cart-updated'));
    } catch (err) {
      console.error('[FavoritesSidebar] Error updating cart quantity:', err);
      toast({
        title: 'Error',
        description: (err as any)?.message || 'No pudimos actualizar la cantidad',
        variant: 'destructive'
      });
    } finally {
      setUpdatingProductIds(prev => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  }, [getCartItemByProductId, handleRemoveFromCart, loadCartItems, session?.user?.id, toast]);

  // Sync local favorites when user logs in
  const handleSyncLocalFavorites = useCallback(async () => {
    setIsSyncing(true);
    try {
      const dbFavs = favorites; // Current favorites in context (from DB)
      const localFavs = loadLocalFavorites(); // Real local favorites from storage

      if (localFavs.size === 0) {
        toast({
          title: '⚠️ Sin favoritos',
          description: 'No hay favoritos guardados para sincronizar',
        });
        return;
      }

      // Filter local favorites that are NOT already in the database list
      const newItemsToSync = Array.from(localFavs.values()).filter(
        item => !dbFavs.some(dbItem => dbItem.id === item.id)
      );

      if (newItemsToSync.length === 0) {
        // This will trigger the context to clean up if we missed it
        await syncLocalFavoritesManually(localFavs);
        setSyncCardDismissed(true);
        return;
      }

      const success = await syncLocalFavoritesManually(localFavs);

      if (success) {
        toast({
          title: '✅ Sincronización completada',
          description: `Se agregaron ${newItemsToSync.length} nuevos favoritos a tu cuenta`,
        });
      } else {
        toast({
          title: 'Error',
          description: 'No pudimos sincronizar tus favoritos guardados',
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('[FavoritesSidebar] Error syncing local favorites:', err);
      toast({
        title: 'Error',
        description: 'No pudimos sincronizar tus favoritos guardados',
        variant: 'destructive',
      });
    } finally {
      setIsSyncing(false);
    }
  }, [favorites, loadLocalFavorites, syncLocalFavoritesManually, toast]);

  // Click fuera para cerrar - DISABLED because the overlay handles it better
  // This hook was interfering with touches inside the sidebar
  // useClickOutsideHandler({
  //   isOpen,
  //   sidebarRef,
  //   handleClose,
  // });

  // Cerrar con tecla ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose]);

  // Limpiar estado "agregando" cuando el producto ya está en el canasto
  useEffect(() => {
    setAddingProductIds(prev => {
      const updated = new Set(prev);
      for (const productId of prev) {
        if (cartItems.some(item => item.product_id === productId)) {
          updated.delete(productId);
        }
      }
      return updated;
    });
  }, [cartItems]);

  // Agrupar favoritos por tienda
  const groupedFavorites: GroupedFavorites = favorites.reduce((acc, product) => {
    const storeId = product.store_id || 'default';
    const storeName = product.store_name || 'Tienda';

    if (!acc[storeId]) {
      acc[storeId] = {
        store_name: storeName,
        products: []
      };
    }

    acc[storeId].products.push(product);
    return acc;
  }, {} as GroupedFavorites);

  const groupEntries = Object.entries(groupedFavorites).sort(([storeIdA], [storeIdB]) => {
    if (!currentStoreId) return 0;
    if (storeIdA === currentStoreId) return -1;
    if (storeIdB === currentStoreId) return 1;
    return 0;
  });

  return (
    <AnimatePresence>
      {(isOpen || isClosing) && (
        <>
          {/* Overlay - Only close if clicking directly on overlay, not from sidebar. */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-transparent z-[9999]"
            onMouseDown={(e) => {
              // Only close if the target is the overlay itself, not something inside it
              if (e.target === e.currentTarget) {
                e.stopPropagation();
                handleClose();
              }
            }}
            onTouchStart={(e) => {
              // Same for touch events
              if (e.target === e.currentTarget) {
                e.stopPropagation();
                handleClose();
              }
            }}
          />

          {/* Sidebar */}
          <motion.div
            ref={sidebarRef}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.2 }}
            className={cn(
              'fixed right-0 w-full lg:w-[400px] bg-background shadow-2xl z-[10000] flex min-h-0 flex-col overflow-x-hidden',
              productSidebarOpen && isDesktop ? 'hidden' : ''
            )}
            style={{
              top: isDesktop ? `${headerHeight}px` : 0,
              bottom: isDesktop ? undefined : 0,
              height: '100%',
            }}
          >
            {/* Safe Area Spacer for Mobile */}
            {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}

            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b shrink-0 bg-primary text-primary-foreground min-h-[64px]">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Heart className="w-5 h-5 fill-red-500 text-red-500" />
                Favoritos
              </h2>
              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleClose(); }}
                        className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
                      >
                        <X className="w-5 h-5 text-white" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent variant="sidebar">
                      <p>Cerrar</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            {/* Content */}
            <ScrollArea
              ref={scrollAreaRef}
              className="min-h-0 flex-1 overflow-x-hidden"
              viewportClassName="h-full max-h-full overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 space-y-6" onClick={(e) => e.stopPropagation()}>

                {/* Sync Card - shown when logged in and has local favorites */}
                {isAuthenticated && hasLocalFavoritesToSync && !syncCardDismissed && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative w-full overflow-hidden flex items-start gap-3 p-4 bg-primary/10 border border-primary/30 rounded-2xl shadow-sm"
                  >
                    <button
                      onClick={() => setSyncCardDismissed(true)}
                      className="absolute top-2 right-2 text-muted-foreground hover:text-foreground transition-colors z-10"
                      aria-label="Cerrar"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                      <Heart className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0 pr-5">
                      <h3 className="text-sm font-bold text-primary leading-tight">
                        ¿Importar favoritos guardados?
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1 leading-normal">
                        Tienes productos favoritos que agregaste sin sesión o sin internet. ¿Quieres sumarlos a tu cuenta?
                      </p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <Button
                          onClick={handleSyncLocalFavorites}
                          disabled={isSyncing}
                          size="sm"
                          className="bg-primary hover:bg-primary/90 text-primary-foreground h-8 px-4 rounded-full text-xs font-bold shadow-sm shadow-primary/20"
                        >
                          {isSyncing ? (
                            <>
                              <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                              Sincronizando...
                            </>
                          ) : (
                            'Sí, importar'
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            localStorage.removeItem('minibid_favorites');
                            window.location.reload();
                          }}
                          disabled={isSyncing}
                          className="h-8 px-3 rounded-full text-xs font-medium text-muted-foreground hover:bg-muted"
                        >
                          Descartar
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                )}

                {groupEntries.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Heart className="h-12 w-12 text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground font-medium">No tienes favoritos aún</p>
                    <p className="text-sm text-muted-foreground">Agrega productos a tu lista de favoritos</p>
                  </div>
                ) : (
                  groupEntries.map(([storeId, storeData]) => (
                    <div key={storeId} className="space-y-3">
                      {/* Store Header with Add All Button */}
                      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/50 rounded-lg border border-border/50">
                        <div className="flex items-center gap-2 flex-1">
                          <StoreIcon className="h-4 w-4 text-primary" />
                          <h3 className="text-sm font-semibold text-foreground">{storeData.store_name}</h3>
                        </div>
                        
                        {/* Add All Button */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleAddAllToCart(storeId, storeData.products); }}
                          disabled={loadingStoreId !== null}
                          className="px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full hover:bg-primary/90 transition-colors active:scale-95 duration-75 whitespace-nowrap disabled:opacity-50 disabled:cursor-wait"
                          title="Agregar todos los productos de esta tienda"
                        >
                          {loadingStoreId === storeId ? (
                            <div className="flex items-center gap-1">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              <span>Agregando...</span>
                            </div>
                          ) : (
                            'Agregar todos'
                          )}
                        </button>
                      </div>

                      {/* Products Grid */}
                      <div className="grid grid-cols-1 gap-3">
                        {storeData.products.map((product) => (
                          <div 
                            key={product.id}
                            className="bg-card rounded-xl border border-border/50 p-3 flex gap-3 group hover:border-primary/30 transition-all shadow-sm hover:shadow-md"
                          >
                            {/* Product Image */}
                            <div className="h-20 w-20 rounded-lg bg-primary/10 flex-shrink-0 relative overflow-hidden flex items-center justify-center">
                              {(() => {
                                const imageUrl = product.main_image_url || product.cover_url || (Array.isArray(product.image_urls) ? product.image_urls[0] : null) || resolvedProductImages[product.id] || null;
                                return imageUrl ? (
                                  <img src={imageUrl} alt={product.title} className="w-full h-full object-cover" />
                                ) : (
                                  <ShoppingCart className="w-10 h-10 text-primary/30" />
                                );
                              })()}
                              
                              {/* Heart Toggle Button */}
                              <div className="absolute top-1 right-1 z-10">
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const wasNotFavorited = !isFavorited(product.id);
                                    toggleFavorite({
                                      id: product.id,
                                      title: product.title,
                                      description: product.description,
                                      fixed_sale_price: product.fixed_sale_price,
                                      stock: product.stock || 0,
                                      addedAt: Date.now(),
                                      store_id: storeId,
                                      store_name: storeData.store_name
                                    });
                                    playFavoriteSound(wasNotFavorited);
                                    vibrate(wasNotFavorited ? 'add' : 'decrease');
                                  }}
                                  size="sm"
                                  variant="ghost"
                                  className="h-10 w-10 p-0 rounded-full shadow-md hover:shadow-lg transition-all active:scale-75 duration-75 bg-white/80 dark:bg-gray-900/80 hover:bg-white dark:hover:bg-gray-800 border-0 flex items-center justify-center shrink-0 aspect-square"
                                >
                                  <Heart 
                                    className={cn(
                                      "h-5 w-5 transition-all",
                                      isFavorited(product.id) ? "fill-red-500 text-red-500" : "text-muted-foreground"
                                    )}
                                  />
                                </Button>
                              </div>
                            </div>

                            {/* Product Info */}
                            <div className="flex-1 min-w-0 flex flex-col justify-between">
                              <div>
                                <button
                                  onClick={() => {
                                    setSelectedProduct(product);
                                    setProductSidebarOpen(true);
                                  }}
                                  className="text-sm font-medium text-foreground line-clamp-1 group-hover:text-primary transition-colors hover:underline text-left cursor-pointer"
                                >
                                  {product.title}
                                </button>
                                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5 leading-relaxed">
                                  {product.description}
                                </p>
                              </div>
                              <div className="flex items-center justify-between mt-2 gap-2">
                                {/* Left: price + in-cart chip */}
                                <div className="flex flex-col gap-0.5 min-w-0">
                                  <span className="text-sm font-bold text-primary">
                                    {formatCurrency(product.fixed_sale_price || 0)}
                                  </span>
                                {getCartItemByProductId(product.id) && (
                                    <span className="inline-flex items-center gap-1 w-fit px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-semibold leading-none">
                                      {addingProductIds.has(product.id) ? (
                                        <>
                                          <Loader2 className="w-2.5 h-2.5 shrink-0 animate-spin" />
                                          Agregando
                                        </>
                                      ) : (
                                        <>
                                          <ShoppingCart className="w-2.5 h-2.5 shrink-0" />
                                          Agregado
                                        </>
                                      )}
                                    </span>
                                  )}
                                </div>
                                {/* Right: fixed-height container prevents shift */}
                                <div className="h-[43px] flex items-center shrink-0" onClick={(e) => e.stopPropagation()}>
                                  {getCartItemByProductId(product.id) ? (
                                    <QuantitySelector
                                      cartItemId={getCartItemByProductId(product.id)?.id || ''}
                                      quantity={getCartItemByProductId(product.id)?.quantity || 1}
                                      onUpdateQuantity={(cartItemId, quantityCalculator) => {
                                        void handleUpdateQuantity(product.id, cartItemId, quantityCalculator);
                                      }}
                                      onPlayBeep={onPlayBeep || (() => {})}
                                      onVibrate={onVibrate || (() => {})}
                                      disabled={
                                        loadingStoreId !== null || 
                                        updatingProductIds.has(product.id) ||
                                        Boolean(getCartItemByProductId(product.id)?.id?.startsWith('pending-'))
                                      }
                                    />
                                  ) : (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleAddToCart(product.id); }}
                                      disabled={loadingStoreId !== null}
                                      className="h-[43px] w-[43px] p-0 rounded-full bg-primary text-white shadow-lg hover:shadow-xl transition-all active:scale-75 duration-75 flex items-center justify-center shrink-0 aspect-square disabled:opacity-50 disabled:cursor-wait"
                                      title="Agregar al canasto (1 unidad)"
                                    >
                                      <Plus className="h-6 w-6" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};